import Grid from '@mui/material/Grid';
import { ReactComponent as TableIcon } from '@Src/assets/images/Table.svg';
import { ReactComponent as MailIcon } from '@Src/assets/images/Email.svg';
import { type Theme, styled } from '@mui/material/styles';

import Reservations from './ReservationForm';
import ContactForm from './ContactForm';
import { useEffect, useState } from 'react';
import { useTranslation } from '@Src/providers/TranslationProvider';
import { ImgixImage } from '@Components/ui/ImgixImage';
import { ChipToggle } from '@Components/ui/ChipToggle';
import {
  type V2ContactPageType,
  type V2ReservationsPageType,
} from '@Src/middlewares/CustomPageTypes';
import StyledAlert from '@Components/ui/StyledAlert';
import { type SubmissionStatus } from './types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isServer } from '@Src/utility/domUtils';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';

const StyledChipToggleGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const StyledImageContainer = styled('div')`
  width: 100%;
  overflow: hidden;
  justify-content: flex-end;
  display: flex;
  ${({ theme }) => theme.breakpoints.down('md')} {
    justify-content: center;
  }
`;

const StyledImageGrid = styled(Grid)`
  padding-left: ${({ theme }) => theme.spacing(6)};
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-left: 0;
    width: 100%;
  }
`;

const StyledAlertGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const StyledGrid = styled(Grid)`
  position: relative;
`;

type StyledAlertContainerProps = {
  ownerState: {
    isSmallScreen: boolean;
  };
};

const StyledAlertContainer = styled('div')<StyledAlertContainerProps>`
  position: ${({ ownerState }) =>
    ownerState?.isSmallScreen ? 'relative' : 'absolute'};
  width: fit-content;
`;

export const ContactForms = ({
  hasContactForm,
  hasReservations,
  reservationsFormDefaultSelected,
  contactFormData,
  reservationsData,
}: {
  hasContactForm?: boolean;
  hasReservations?: boolean;
  reservationsFormDefaultSelected?: boolean;
  contactFormData?: V2ContactPageType;
  reservationsData?: V2ReservationsPageType;
}): JSX.Element => {
  const { translate } = useTranslation();
  const { imageAttributes } = useDesignSchema('v2');
  const [reservationFormSelected, setReservationFormSelected] =
    useState<boolean>(!!reservationsFormDefaultSelected && !!hasReservations);

  const [contactFormSubmissionStatus, setContactFormSubmissionStatus] =
    useState<SubmissionStatus>('none');
  const [
    reservationsFormSubmissionStatus,
    setReservationsFormSubmissionStatus,
  ] = useState<SubmissionStatus>('none');
  const isMediumScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const isOnlySmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between('xs', 'sm'),
  );
  const isOnlyMediumScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between('sm', 'md'),
  );

  let image = '';
  if (hasReservations && hasContactForm) {
    image = contactFormData?.image || reservationsData?.image || '';
  } else if (hasReservations) {
    image = reservationsData?.image || '';
  } else if (hasContactForm) {
    image = contactFormData?.image || '';
  }

  const getAlert = (): JSX.Element => {
    if (
      reservationsFormSubmissionStatus === 'success' &&
      reservationFormSelected
    ) {
      return (
        <StyledAlertContainer ownerState={{ isSmallScreen: isMediumScreen }}>
          <StyledAlert
            severity="success"
            message={translate('Reservation_submitted')}
          />
        </StyledAlertContainer>
      );
    } else if (
      reservationsFormSubmissionStatus === 'error' &&
      reservationFormSelected
    ) {
      return (
        <StyledAlertContainer ownerState={{ isSmallScreen: isMediumScreen }}>
          <StyledAlert
            severity="error"
            message={translate('There_has_been_issue')}
          />
        </StyledAlertContainer>
      );
    } else if (
      contactFormSubmissionStatus === 'success' &&
      !reservationFormSelected
    ) {
      return (
        <StyledAlertContainer ownerState={{ isSmallScreen: isMediumScreen }}>
          <StyledAlert
            severity="success"
            message={translate('Your_message_has_been_successfully_sent')}
          />
        </StyledAlertContainer>
      );
    } else if (
      contactFormSubmissionStatus === 'error' &&
      !reservationFormSelected
    ) {
      return (
        <StyledAlertContainer ownerState={{ isSmallScreen: isMediumScreen }}>
          <StyledAlert
            severity="error"
            message={translate('Sending_message_failed_try_again_later')}
          />
        </StyledAlertContainer>
      );
    } else {
      return <></>;
    }
  };
  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  let imageSize = 534;
  if (!isServer && window?.innerWidth < 500) {
    imageSize = 500;
  } else if (isOnlySmallScreen || isOnlyMediumScreen) {
    imageSize = 768;
  }

  return (
    <Grid container>
      <StyledGrid item xs={12} md={image ? 6 : 7}>
        <Grid container>
          {hasContactForm && hasReservations && (
            <StyledChipToggleGrid item xs={12}>
              <ChipToggle
                marginLeft={'none'}
                LeftIcon={TableIcon}
                RightIcon={MailIcon}
                handleLeftOnClick={() => setReservationFormSelected(true)}
                handleRightOnClick={() => setReservationFormSelected(false)}
                leftLabel={translate('Book_a_table')}
                rightLabel={translate('Contact_us')}
                leftSelected={reservationFormSelected}
              />
            </StyledChipToggleGrid>
          )}
          {!reservationFormSelected && (
            <Grid item xs={12}>
              <ContactForm
                formSubmissionStatus={contactFormSubmissionStatus}
                setFormSubmissionStatus={setContactFormSubmissionStatus}
              />
            </Grid>
          )}
          {reservationFormSelected && (
            <Grid item xs={12}>
              <Reservations
                daysAhead={reservationsData?.daysAhead}
                setFormSubmissionStatus={setReservationsFormSubmissionStatus}
                submissionStatus={reservationsFormSubmissionStatus}
              />
            </Grid>
          )}
        </Grid>
        {!isMediumScreen && getAlert()}
      </StyledGrid>
      {isMediumScreen && (
        <StyledAlertGrid item xs={12}>
          {getAlert()}
        </StyledAlertGrid>
      )}
      {image && (
        <StyledImageGrid item xs={12} sm={12} md={6}>
          <StyledImageContainer>
            <ImgixImage
              loading="lazy"
              src={
                imageAttributes?.[image]?.alt ||
                `${image || ''}?w=${
                  isFirstRender ? 534 : imageSize
                }&ar=3:2&fit=crop&crop=entropy`
              }
              alt={`contact-page-image`}
            />
          </StyledImageContainer>
        </StyledImageGrid>
      )}
    </Grid>
  );
};

export default ContactForms;
